import { createContext, useContext, useEffect, useState } from 'react';
import { addToCart, clearCart, getCart, removeCartItem, updateCartItem } from '../lib/api';
import { getCartSessionId } from '../lib/cartSession';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

const emptyCart = {
  cartId: null,
  sessionId: null,
  itemCount: 0,
  subtotal: 0,
  items: [],
};

export function CartProvider({ children }) {
  const [cart, setCart] = useState(emptyCart);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [sessionId] = useState(() => getCartSessionId());
  const { user } = useAuth();
  const userId = user?.id || '';

  useEffect(() => {
    let isMounted = true;

    async function loadCart() {
      setIsLoading(true);
      try {
        const nextCart = await getCart({ sessionId, userId });
        if (isMounted) {
          setCart(nextCart);
          setError('');
        }
      } catch (loadError) {
        if (isMounted) {
          setError(loadError.message);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadCart();

    return () => {
      isMounted = false;
    };
  }, [sessionId, userId]);

  async function runCartAction(action, options = {}) {
    try {
      const nextCart = await action();
      setCart(nextCart);
      setError('');

      if (options.openDrawer) {
        setIsCartOpen(true);
      }

      return nextCart;
    } catch (actionError) {
      setError(actionError.message);
      throw actionError;
    }
  }

  const value = {
    cart,
    cartError: error,
    cartIsLoading: isLoading,
    isCartOpen,
    openCart() {
      setIsCartOpen(true);
    },
    closeCart() {
      setIsCartOpen(false);
    },
    toggleCart() {
      setIsCartOpen((current) => !current);
    },
    addItem(productId, quantity = 1) {
      return runCartAction(() =>
        addToCart({
          sessionId,
          userId,
          productId,
          quantity,
        }),
        { openDrawer: true }
      );
    },
    updateItem(itemId, quantity) {
      return runCartAction(() =>
        updateCartItem({
          itemId,
          quantity,
          sessionId,
          userId,
        })
      );
    },
    removeItem(itemId) {
      return runCartAction(() =>
        removeCartItem({
          itemId,
          sessionId,
          userId,
        })
      );
    },
    clearAll() {
      return runCartAction(() => clearCart({ sessionId, userId }));
    },
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error('useCart must be used inside a CartProvider');
  }

  return context;
}
